export const SUBSCRIBER_CSV_HEADERS = [
    "Name",
    "Customer number",
    "Phone number",
    "Reveknew reference",
    "Segment",
    "Tags",
];

const TAG_NAME_DELIMITER = ", ";
const IDENTITY_HEADER_KEYS = new Set([
    "name",
    "customer number",
    "phone number",
    "reveknew reference",
    "reference",
    "reveknew",
    "segment",
    "tags",
]);

export const stripBom = (value = "") => String(value).replace(/^\ufeff/, "");

export const escapeCsvValue = (value) => {
    if (value == null || value === "") {
        return "";
    }

    const stringValue = String(value);
    if (/[",\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
};

export const parseCsv = (text) => {
    const input = stripBom(String(text ?? "")).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    if (!input) {
        return [];
    }

    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < input.length; i += 1) {
        const char = input[i];

        if (inQuotes) {
            if (char === '"') {
                if (input[i + 1] === '"') {
                    field += '"';
                    i += 1;
                } else {
                    inQuotes = false;
                }
            } else {
                field += char;
            }
            continue;
        }

        if (char === '"') {
            inQuotes = true;
        } else if (char === ",") {
            row.push(field);
            field = "";
        } else if (char === "\n") {
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
        } else {
            field += char;
        }
    }

    if (inQuotes || field.length > 0 || row.length > 0) {
        row.push(field);
        rows.push(row);
    }

    if (rows.length && rows[rows.length - 1].every((cell) => cell === "")) {
        rows.pop();
    }

    return rows;
};

export const uniqueTagNames = (tags = []) => {
    const names = [];
    const seen = new Set();
    const tagList = Array.isArray(tags) ? tags : [];

    tagList.forEach((tag) => {
        const name = typeof tag === "string" ? tag.trim() : String(tag?.name || "").trim();
        if (!name || seen.has(name)) {
            return;
        }
        seen.add(name);
        names.push(name);
    });

    return names;
};

const normalizeHeaderKey = (header = "") => stripBom(header).trim().toLowerCase();

const findHeaderIndex = (headers = [], aliases = []) =>
    headers.findIndex((header) => aliases.includes(normalizeHeaderKey(header)));

const cellAt = (row = [], index) => (index >= 0 ? String(row[index] ?? "").trim() : "");

const joinTagValues = (values = []) => uniqueTagNames(values).join(TAG_NAME_DELIMITER);

const serializeCsv = (rows = []) =>
    rows
        .map((row) => row.map(escapeCsvValue).join(","))
        .join("\n");

export const buildSubscriberCsv = (rows = []) =>
    serializeCsv([
        SUBSCRIBER_CSV_HEADERS,
        ...rows.map((row) => [
            row.name,
            row.customerNum,
            row.phoneNo,
            row.reference,
            row.segment,
            Array.isArray(row.tags) ? joinTagValues(row.tags) : row.tags,
        ]),
    ]);

export const buildSelectedCustomersCsv = (customers = []) =>
    buildSubscriberCsv(
        customers.map((row) => {
            const customer = row.businessCustomer || {};
            return {
                name: customer.name,
                customerNum: customer.customerNum,
                phoneNo: customer.phoneNo,
                reference: customer.reference,
                segment: customer.segment?.name,
                tags: uniqueTagNames(row.tags),
            };
        })
    );

export const normalizeSubscriberExportCsv = (csvText = "") => {
    const rows = parseCsv(csvText);
    if (!rows.length) {
        return `${SUBSCRIBER_CSV_HEADERS.join(",")}\n`;
    }

    const headers = rows[0] || [];
    const nameIndex = findHeaderIndex(headers, ["name"]);
    const customerNumIndex = findHeaderIndex(headers, ["customer number"]);
    const phoneIndex = findHeaderIndex(headers, ["phone number", "phone no"]);
    const referenceIndex = findHeaderIndex(headers, ["reveknew reference", "reference", "reveknew"]);
    const segmentIndex = findHeaderIndex(headers, ["segment"]);
    const tagsIndex = findHeaderIndex(headers, ["tags"]);
    const tagGroupIndexes = headers
        .map((header, index) => ({ header, index }))
        .filter(({ header }) => !IDENTITY_HEADER_KEYS.has(normalizeHeaderKey(header)))
        .map(({ index }) => index);

    const dataRows = rows.slice(1).map((row) => {
        const groupedTags = tagGroupIndexes
            .map((index) => cellAt(row, index))
            .filter(Boolean);
        const existingTags = tagsIndex >= 0
            ? cellAt(row, tagsIndex).split(TAG_NAME_DELIMITER)
            : [];

        return [
            cellAt(row, nameIndex),
            cellAt(row, customerNumIndex),
            cellAt(row, phoneIndex),
            cellAt(row, referenceIndex),
            cellAt(row, segmentIndex),
            joinTagValues([...existingTags, ...groupedTags]),
        ];
    });

    return serializeCsv([SUBSCRIBER_CSV_HEADERS, ...dataRows]);
};

export const readCsvText = async (data) => {
    if (data == null) {
        return "";
    }
    if (typeof data === "string") {
        return data;
    }
    if (typeof data.text === "function") {
        return data.text();
    }
    if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
        return new TextDecoder("utf-8").decode(data);
    }
    return String(data);
};
